import { useState, useEffect } from "react";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { CreditCard, Plus, FileText, Trash2, Download, Layers, Mail, Sparkles, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultPaymentForm = { client_id: "", device_id: "", amount: "", currency: "ALL", payment_date: new Date().toISOString().substring(0, 10), method: "cash", reference: "", notes: "" };
const defaultBulkForm = { client_id: "", device_ids: [], amount_per_device: "", currency: "ALL", payment_date: new Date().toISOString().substring(0, 10), method: "bank_transfer", period_label: "", notes: "" };

function fmt(n) { return new Intl.NumberFormat("sq-AL").format(Math.round(n || 0)); }

export default function PaymentsPage() {
    const { t } = useLang();
    const { user } = useAuth();
    const [payments, setPayments] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [devices, setDevices] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkForm, setBulkForm] = useState(defaultBulkForm);
    const [paymentForm, setPaymentForm] = useState(defaultPaymentForm);
    const [invoiceClientId, setInvoiceClientId] = useState("");
    const [invoiceItems, setInvoiceItems] = useState([{ description: "", quantity: 1, unit_price: "" }]);
    const [invoiceCurrency, setInvoiceCurrency] = useState("ALL");
    const [invoiceTax, setInvoiceTax] = useState("20");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState("payments");

    const canEdit = ["admin", "staff"].includes(user?.role);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [pRes, iRes, cRes, dRes] = await Promise.all([
                axios.get(`${API}/payments`, { params: { limit: 100 }, withCredentials: true }),
                axios.get(`${API}/invoices`, { params: { limit: 100 }, withCredentials: true }),
                axios.get(`${API}/clients`, { params: { limit: 500 }, withCredentials: true }),
                axios.get(`${API}/devices`, { params: { limit: 1000 }, withCredentials: true }),
            ]);
            setPayments(pRes.data.items); setTotal(pRes.data.total);
            setInvoices(iRes.data.items);
            setClients(cRes.data.items);
            setDevices(dRes.data.items);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => { fetchAll(); }, []);

    const handlePaymentSubmit = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            const payload = { ...paymentForm, amount: parseFloat(paymentForm.amount) };
            if (!payload.device_id) delete payload.device_id;
            if (!payload.reference) delete payload.reference;
            await axios.post(`${API}/payments`, payload, { withCredentials: true });
            setShowPaymentModal(false); setPaymentForm(defaultPaymentForm); fetchAll();
        } catch (e) { setError(e.response?.data?.detail || t("error")); }
        setSaving(false);
    };

    const handleInvoiceSubmit = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            const validItems = invoiceItems.filter(i => i.description && i.unit_price);
            if (validItems.length === 0) { setError("Shto të paktën një artikull"); setSaving(false); return; }
            const payload = {
                client_id: invoiceClientId,
                items: validItems.map(i => ({ ...i, quantity: Number(i.quantity), unit_price: parseFloat(i.unit_price) })),
                currency: invoiceCurrency,
                tax_rate: parseFloat(invoiceTax)
            };
            await axios.post(`${API}/invoices`, payload, { withCredentials: true });
            setShowInvoiceModal(false); fetchAll();
        } catch (e) { setError(e.response?.data?.detail || t("error")); }
        setSaving(false);
    };

    const downloadPdf = async (invoice) => {
        try {
            const res = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
                withCredentials: true,
                responseType: "blob"
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `fatura-${invoice.invoice_number}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) { alert("Gabim gjatë gjenerimit të PDF"); }
    };

    const sendInvoiceEmail = async (invoice) => {
        const client = clients.find(c => c.id === invoice.client_id);
        const defaultEmail = client?.email || invoice.client_email || "";
        const toEmail = window.prompt(
            `Dërgo faturën ${invoice.invoice_number} me PDF bashkëlidhur tek:`,
            defaultEmail
        );
        if (!toEmail) return;
        try {
            await axios.post(`${API}/invoices/${invoice.id}/send-email`,
                { to_email: toEmail },
                { withCredentials: true });
            alert(`✓ Fatura ${invoice.invoice_number} u dërgua tek ${toEmail}`);
        } catch (e) {
            alert("Gabim: " + (e.response?.data?.detail || "Dërgimi dështoi"));
        }
    };

    const handleDeletePayment = async (id) => {
        if (!window.confirm(t("confirm_delete"))) return;
        try { await axios.delete(`${API}/payments/${id}`, { withCredentials: true }); fetchAll(); } catch (e) { alert(t("error")); }
    };

    const handleBulkSubmit = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            if (!bulkForm.client_id) { setError("Zgjidh klientin"); setSaving(false); return; }
            if (bulkForm.device_ids.length === 0) { setError("Zgjidh të paktën një pajisje"); setSaving(false); return; }
            const payload = {
                client_id: bulkForm.client_id,
                device_ids: bulkForm.device_ids,
                amount_per_device: parseFloat(bulkForm.amount_per_device),
                currency: bulkForm.currency,
                payment_date: bulkForm.payment_date,
                method: bulkForm.method,
                period_label: bulkForm.period_label || null,
                notes: bulkForm.notes || null,
            };
            const { data } = await axios.post(`${API}/payments/bulk`, payload, { withCredentials: true });
            setShowBulkModal(false); setBulkForm(defaultBulkForm);
            alert(`U regjistruan ${data.count} pagesa. Totali: ${data.total_amount} ${data.currency}`);
            fetchAll();
        } catch (e) { setError(e.response?.data?.detail || t("error")); }
        setSaving(false);
    };

    const handleExportPayments = async () => {
        try {
            const res = await axios.get(`${API}/export/payments.csv`, { withCredentials: true, responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `pagesat-${new Date().toISOString().substring(0, 10)}.csv`);
            document.body.appendChild(link); link.click(); link.remove();
        } catch { alert("Gabim gjatë eksportimit"); }
    };

    const bulkClientDevices = devices.filter(d => d.client_id === bulkForm.client_id);
    const bulkTotal = bulkForm.device_ids.length * parseFloat(bulkForm.amount_per_device || 0);

    const toggleBulkDevice = (id) => {
        setBulkForm(prev => ({
            ...prev,
            device_ids: prev.device_ids.includes(id)
                ? prev.device_ids.filter(x => x !== id)
                : [...prev.device_ids, id]
        }));
    };

    const toggleAllBulkDevices = () => {
        setBulkForm(prev => ({
            ...prev,
            device_ids: prev.device_ids.length === bulkClientDevices.length ? [] : bulkClientDevices.map(d => d.id)
        }));
    };

    const bf = (key, val) => setBulkForm(prev => ({ ...prev, [key]: val }));

    const pf = (key, val) => setPaymentForm(prev => ({ ...prev, [key]: val }));

    const addInvoiceItem = () => setInvoiceItems(prev => [...prev, { description: "", quantity: 1, unit_price: "" }]);
    const updateItem = (i, key, val) => setInvoiceItems(prev => prev.map((item, idx) => idx === i ? { ...item, [key]: val } : item));
    const removeItem = (i) => setInvoiceItems(prev => prev.filter((_, idx) => idx !== i));

    const invoiceTotal = invoiceItems.reduce((sum, i) => sum + (Number(i.quantity) * parseFloat(i.unit_price || 0)), 0);
    const invoiceTaxAmt = invoiceTotal * (parseFloat(invoiceTax || 0) / 100);

    const INVOICE_STATUS = {
        paid: "bg-emerald-50 text-emerald-700 border border-emerald-200",
        unpaid: "bg-rose-50 text-rose-700 border border-rose-200",
        overdue: "bg-amber-50 text-amber-700 border border-amber-200",
        draft: "bg-slate-100 text-slate-600 border border-slate-200",
        sent: "bg-sky-50 text-sky-700 border border-sky-200",
    };
    const INVOICE_STATUS_LABEL = {
        paid: "E paguar",
        unpaid: "Pa paguar",
        overdue: "E vonuar",
        draft: "Draft",
        sent: "Dërguar",
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <CreditCard size={20} className="text-[#E7212E]" /> {t("payments")}
                    </h1>
                </div>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        <button onClick={handleExportPayments} data-testid="export-payments-btn"
                            className="flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium px-3 py-2 rounded transition-colors">
                            <Download size={14} /> CSV
                        </button>
                        <button onClick={async () => {
                            const today = new Date();
                            const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
                            const period = window.prompt(
                                "Gjenero faturat mujore për periudhën (YYYY-MM).\n\nDo krijohet 1 faturë për çdo klient me abonime aktive, duke grupuar të gjitha IMEI-t në një faturë multi-pajisje. Faturat dërgohen automatikisht me email + PDF.",
                                defaultMonth
                            );
                            if (!period) return;
                            if (!/^\d{4}-\d{2}$/.test(period)) { alert("Formati duhet YYYY-MM (p.sh. 2026-02)"); return; }
                            try {
                                const { data } = await axios.post(`${API}/invoices/generate-monthly`,
                                    { period_month: period, due_days: 30 },
                                    { withCredentials: true });
                                alert(`✓ U krijuan ${data.created_count} fatura për ${data.period_label}.\n${data.emails_sent} email të dërguara.\n${data.skipped_count} fatura u kaluan (ekzistojnë).`);
                                fetchAll();
                            } catch (e) { alert("Gabim: " + (e.response?.data?.detail || "")); }
                        }} data-testid="generate-monthly-btn"
                            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                            <Sparkles size={15} /> Gjenero Faturat
                        </button>
                        <button onClick={() => { setShowInvoiceModal(true); setError(""); setInvoiceItems([{ description: "", quantity: 1, unit_price: "" }]); }}
                            data-testid="add-invoice-btn"
                            className="flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium px-4 py-2 rounded transition-colors">
                            <FileText size={15} /> {t("add_invoice")}
                        </button>
                        <button onClick={() => { setShowBulkModal(true); setError(""); setBulkForm(defaultBulkForm); }}
                            data-testid="add-bulk-payment-btn"
                            className="flex items-center gap-2 border border-[#E7212E] text-[#E7212E] hover:bg-[#E7212E]/5 text-sm font-medium px-4 py-2 rounded transition-colors">
                            <Layers size={15} /> Bulk Pagesa
                        </button>
                        <button onClick={() => { setShowPaymentModal(true); setError(""); setPaymentForm(defaultPaymentForm); }}
                            data-testid="add-payment-btn"
                            className="flex items-center gap-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                            <Plus size={15} /> {t("register_payment")}
                        </button>
                    </div>
                )}
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                    <TabsTrigger value="payments" data-testid="tab-payments">{t("payments")} ({total})</TabsTrigger>
                    <TabsTrigger value="invoices" data-testid="tab-invoices">{t("invoices")} ({invoices.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="payments">
                    <div className="bg-white border border-slate-200 rounded overflow-hidden">
                        <table className="w-full crm-table" data-testid="payments-table">
                            <thead>
                                <tr>
                                    <th>{t("client")}</th>
                                    <th>IMEI</th>
                                    <th>{t("amount")}</th>
                                    <th>{t("method")}</th>
                                    <th>{t("date")}</th>
                                    <th>{t("notes")}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={7} className="text-center py-10 text-slate-400">{t("loading")}</td></tr>
                                ) : payments.length === 0 ? (
                                    <tr><td colSpan={7} className="text-center py-10 text-slate-400">{t("no_data")}</td></tr>
                                ) : payments.map(p => (
                                    <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                                        <td className="font-medium text-sm">{p.client_name}</td>
                                        <td className="font-mono text-xs text-slate-600">{p.imei || "—"}</td>
                                        <td className="font-mono font-semibold text-emerald-600">{fmt(p.amount)} Lek</td>
                                        <td className="capitalize text-sm">{t(p.method) || p.method}</td>
                                        <td className="text-xs text-slate-500">{p.payment_date?.substring(0, 10)}</td>
                                        <td className="text-xs text-slate-500 max-w-32 truncate">{p.notes || "—"}</td>
                                        <td>
                                            {user?.role === "admin" && (
                                                <button onClick={() => handleDeletePayment(p.id)}
                                                    className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600" data-testid={`delete-payment-${p.id}`}>
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="invoices">
                    <div className="bg-white border border-slate-200 rounded overflow-hidden">
                        <table className="w-full crm-table" data-testid="invoices-table">
                            <thead>
                                <tr>
                                    <th>{t("invoice_number")}</th>
                                    <th>{t("client")}</th>
                                    <th>{t("total")}</th>
                                    <th>{t("issue_date")}</th>
                                    <th>{t("due_date")}</th>
                                    <th>{t("status")}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={7} className="text-center py-10 text-slate-400">{t("loading")}</td></tr>
                                ) : invoices.length === 0 ? (
                                    <tr><td colSpan={7} className="text-center py-10 text-slate-400">{t("no_data")}</td></tr>
                                ) : invoices.map(inv => (
                                    <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                                        <td className="font-mono text-sm font-medium text-[#E7212E]">{inv.invoice_number}</td>
                                        <td className="font-medium text-sm">{inv.client_name}</td>
                                        <td className="font-mono font-semibold">{fmt(inv.total)} Lek</td>
                                        <td className="text-xs text-slate-500">{inv.issue_date?.substring(0, 10)}</td>
                                        <td className="text-xs text-slate-500">{inv.due_date?.substring(0, 10)}</td>
                                        <td>
                                            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${INVOICE_STATUS[inv.status] || INVOICE_STATUS.unpaid}`}>
                                                {INVOICE_STATUS_LABEL[inv.status] || inv.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                {inv.status !== "paid" && (
                                                    <button onClick={async () => {
                                                        if (!window.confirm(`Shëno faturën ${inv.invoice_number} si të paguar?`)) return;
                                                        try {
                                                            await axios.post(`${API}/invoices/${inv.id}/mark-paid`, {}, { withCredentials: true });
                                                            fetchAll();
                                                        } catch { alert("Gabim"); }
                                                    }}
                                                        data-testid={`mark-paid-${inv.id}`}
                                                        title="Shëno si të paguar"
                                                        className="flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-50 hover:bg-emerald-600 hover:text-white text-emerald-700 text-xs font-medium transition-colors">
                                                        <CheckCircle2 size={12} /> Paguar
                                                    </button>
                                                )}
                                                <button onClick={() => sendInvoiceEmail(inv)}
                                                    data-testid={`email-invoice-${inv.id}`}
                                                    title="Dërgo me email"
                                                    className="flex items-center gap-1 px-2.5 py-1 rounded bg-slate-100 hover:bg-[#E7212E] hover:text-white text-slate-700 text-xs font-medium transition-colors">
                                                    <Mail size={12} /> Email
                                                </button>
                                                <button onClick={() => downloadPdf(inv)}
                                                    data-testid={`download-invoice-${inv.id}`}
                                                    className="flex items-center gap-1 px-2.5 py-1 rounded bg-slate-100 hover:bg-[#E7212E] hover:text-white text-slate-700 text-xs font-medium transition-colors">
                                                    <Download size={12} /> PDF
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Payment Modal */}
            <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>{t("register_payment")}</DialogTitle></DialogHeader>
                    <form onSubmit={handlePaymentSubmit} className="space-y-3 mt-2">
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("client")} *</label>
                            <Select value={paymentForm.client_id || "none"} onValueChange={v => pf("client_id", v === "none" ? "" : v)}>
                                <SelectTrigger data-testid="payment-client-select"><SelectValue placeholder="Zgjidh klientin" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— Zgjidh —</SelectItem>
                                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Pajisja (Opsionale)</label>
                            <Select value={paymentForm.device_id || "none"} onValueChange={v => pf("device_id", v === "none" ? "" : v)}>
                                <SelectTrigger><SelectValue placeholder="Zgjidh pajisjen" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— Pa pajisje specifike —</SelectItem>
                                    {devices.filter(d => !paymentForm.client_id || d.client_id === paymentForm.client_id).map(d => (
                                        <SelectItem key={d.id} value={d.id}>{d.imei}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("amount")} *</label>
                                <input type="number" step="0.01" required value={paymentForm.amount} onChange={e => pf("amount", e.target.value)}
                                    data-testid="payment-amount-input"
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("currency")}</label>
                                <div className="px-3 py-2 border border-slate-200 rounded text-sm bg-slate-50 text-slate-700 font-medium">Lek</div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("date")}</label>
                                <input type="date" required value={paymentForm.payment_date} onChange={e => pf("payment_date", e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("method")}</label>
                                <Select value={paymentForm.method} onValueChange={v => pf("method", v)}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cash">{t("cash")}</SelectItem>
                                        <SelectItem value="bank_transfer">{t("bank_transfer")}</SelectItem>
                                        <SelectItem value="card">{t("card")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("notes")}</label>
                            <input value={paymentForm.notes} onChange={e => pf("notes", e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                        </div>
                        {error && <p className="text-rose-600 text-sm bg-rose-50 p-2 rounded">{error}</p>}
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowPaymentModal(false)}
                                className="px-4 py-2 border border-slate-200 rounded text-sm text-slate-700 hover:bg-slate-50">{t("cancel")}</button>
                            <button type="submit" disabled={saving} data-testid="save-payment-btn"
                                className="px-4 py-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium rounded disabled:opacity-60">
                                {saving ? "Duke ruajtur..." : t("save")}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Invoice Modal */}
            <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{t("add_invoice")}</DialogTitle></DialogHeader>
                    <form onSubmit={handleInvoiceSubmit} className="space-y-4 mt-2">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("client")} *</label>
                                <Select value={invoiceClientId || "none"} onValueChange={v => setInvoiceClientId(v === "none" ? "" : v)}>
                                    <SelectTrigger data-testid="invoice-client-select"><SelectValue placeholder="Zgjidh klientin" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— Zgjidh —</SelectItem>
                                        {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} {c.nipt ? `(${c.nipt})` : ""}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">TVSH (%)</label>
                                <input type="number" value={invoiceTax} onChange={e => setInvoiceTax(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Artikujt</label>
                                <button type="button" onClick={addInvoiceItem} className="text-xs text-[#E7212E] hover:underline">+ Shto artikull</button>
                            </div>
                            <div className="space-y-2">
                                {invoiceItems.map((item, i) => (
                                    <div key={i} className="flex gap-2 items-end">
                                        <div className="flex-1">
                                            {i === 0 && <label className="text-xs text-slate-400 block mb-1">Përshkrimi</label>}
                                            <input placeholder="Përshkrim artikulli" value={item.description} onChange={e => updateItem(i, "description", e.target.value)}
                                                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                                        </div>
                                        <div className="w-16">
                                            {i === 0 && <label className="text-xs text-slate-400 block mb-1">Sasia</label>}
                                            <input type="number" value={item.quantity} onChange={e => updateItem(i, "quantity", e.target.value)}
                                                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                                        </div>
                                        <div className="w-28">
                                            {i === 0 && <label className="text-xs text-slate-400 block mb-1">Çmimi</label>}
                                            <input type="number" step="0.01" placeholder="0.00" value={item.unit_price} onChange={e => updateItem(i, "unit_price", e.target.value)}
                                                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                                        </div>
                                        <div className="w-24 pb-0">
                                            {i === 0 && <label className="text-xs text-slate-400 block mb-1">Totali</label>}
                                            <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm font-mono text-slate-600">
                                                {fmt(Number(item.quantity) * parseFloat(item.unit_price || 0))}
                                            </div>
                                        </div>
                                        {i > 0 && (
                                            <button type="button" onClick={() => removeItem(i)} className="p-2 text-slate-400 hover:text-rose-500 mb-0">
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-between items-end pt-2 border-t border-slate-200">
                            <div className="text-sm space-y-0.5 text-slate-600">
                                <p>Nëntotali: <span className="font-mono font-medium">{fmt(invoiceTotal)} Lek</span></p>
                                <p>TVSH ({invoiceTax}%): <span className="font-mono font-medium">{fmt(invoiceTaxAmt)} Lek</span></p>
                                <p className="font-bold text-slate-900">Totali: <span className="font-mono text-[#E7212E]">{fmt(invoiceTotal + invoiceTaxAmt)} Lek</span></p>
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setShowInvoiceModal(false)}
                                    className="px-4 py-2 border border-slate-200 rounded text-sm text-slate-700 hover:bg-slate-50">{t("cancel")}</button>
                                <button type="submit" disabled={saving || !invoiceClientId} data-testid="save-invoice-btn"
                                    className="px-4 py-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium rounded disabled:opacity-60">
                                    {saving ? "Duke krijuar..." : "Krijo Faturën"}
                                </button>
                            </div>
                        </div>
                        {error && <p className="text-rose-600 text-sm bg-rose-50 p-2 rounded">{error}</p>}
                    </form>
                </DialogContent>
            </Dialog>
            {/* Bulk Payment Modal */}
            <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Regjistro Pagesë për Shumë Pajisje</DialogTitle></DialogHeader>
                    <form onSubmit={handleBulkSubmit} className="space-y-3 mt-2">
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("client")} *</label>
                            <Select value={bulkForm.client_id || "none"} onValueChange={v => { bf("client_id", v === "none" ? "" : v); bf("device_ids", []); }}>
                                <SelectTrigger data-testid="bulk-client-select"><SelectValue placeholder="Zgjidh klientin" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— Zgjidh —</SelectItem>
                                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} {c.nipt ? `(${c.nipt})` : ""}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        {bulkForm.client_id && (
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pajisjet ({bulkForm.device_ids.length}/{bulkClientDevices.length})</label>
                                    {bulkClientDevices.length > 0 && (
                                        <button type="button" onClick={toggleAllBulkDevices} data-testid="bulk-toggle-all"
                                            className="text-xs text-[#E7212E] hover:underline">
                                            {bulkForm.device_ids.length === bulkClientDevices.length ? "Hiq të gjitha" : "Zgjidh të gjitha"}
                                        </button>
                                    )}
                                </div>
                                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
                                    {bulkClientDevices.length === 0 ? (
                                        <div className="text-xs text-slate-400 p-3 text-center">Asnjë pajisje për këtë klient</div>
                                    ) : bulkClientDevices.map(d => (
                                        <label key={d.id} className="flex items-center gap-3 p-2.5 hover:bg-slate-50 cursor-pointer text-sm">
                                            <input type="checkbox"
                                                data-testid={`bulk-device-${d.id}`}
                                                checked={bulkForm.device_ids.includes(d.id)}
                                                onChange={() => toggleBulkDevice(d.id)}
                                                className="w-4 h-4 accent-[#E7212E]" />
                                            <span className="font-mono text-xs text-slate-700 flex-1">{d.imei}</span>
                                            <span className="text-xs text-slate-500">{d.model || "—"}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Shuma / pajisje *</label>
                                <input type="number" step="0.01" required value={bulkForm.amount_per_device} onChange={e => bf("amount_per_device", e.target.value)}
                                    data-testid="bulk-amount-input"
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("currency")}</label>
                                <div className="px-3 py-2 border border-slate-200 rounded text-sm bg-slate-50 text-slate-700 font-medium">Lek</div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Periudha</label>
                                <input placeholder="Shkurt 2026" value={bulkForm.period_label} onChange={e => bf("period_label", e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("date")}</label>
                                <input type="date" required value={bulkForm.payment_date} onChange={e => bf("payment_date", e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("method")}</label>
                                <Select value={bulkForm.method} onValueChange={v => bf("method", v)}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cash">{t("cash")}</SelectItem>
                                        <SelectItem value="bank_transfer">{t("bank_transfer")}</SelectItem>
                                        <SelectItem value="card">{t("card")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("notes")}</label>
                            <input value={bulkForm.notes} onChange={e => bf("notes", e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                        </div>

                        {bulkForm.device_ids.length > 0 && bulkForm.amount_per_device && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
                                <span className="text-slate-700">Totali: </span>
                                <span className="font-mono font-bold text-emerald-700 text-base">
                                    {fmt(bulkTotal)} Lek
                                </span>
                                <span className="text-xs text-slate-500 ml-2">({bulkForm.device_ids.length} pajisje × {fmt(parseFloat(bulkForm.amount_per_device))} Lek)</span>
                            </div>
                        )}

                        {error && <p className="text-rose-600 text-sm bg-rose-50 p-2 rounded">{error}</p>}
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowBulkModal(false)}
                                className="px-4 py-2 border border-slate-200 rounded text-sm text-slate-700 hover:bg-slate-50">{t("cancel")}</button>
                            <button type="submit" disabled={saving || bulkForm.device_ids.length === 0} data-testid="save-bulk-payment-btn"
                                className="px-4 py-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium rounded disabled:opacity-60">
                                {saving ? "Duke regjistruar..." : `Regjistro ${bulkForm.device_ids.length} pagesa`}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
